import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  Copy,
  KeyRound,
  Lock,
  Plus,
  RefreshCw,
  RotateCcw,
  Trash2,
  X,
} from "lucide-react";
import SideBar from "./SideBar";
import UserProfileMenu from "./UserProfileMenu";
import { authFetch, getStoredAccessToken } from "../utils/auth";

const getErrorMessageFromResponse = async (response, fallbackMessage) => {
  const contentType = (response.headers.get("content-type") || "").toLowerCase();
  let detail = "";

  if (contentType.includes("application/json")) {
    const payload = await response.json().catch(() => ({}));
    if (payload && typeof payload === "object") {
      if (typeof payload.detail === "string" && payload.detail.trim()) {
        detail = payload.detail.trim();
      } else if (Array.isArray(payload.detail) && payload.detail.length > 0) {
        const firstIssue = payload.detail[0];
        if (firstIssue && typeof firstIssue === "object") {
          const firstMsg = String(firstIssue.msg || "").trim();
          const normalized = firstMsg.replace(/^value error,\s*/i, "").trim();
          if (normalized) {
            detail = normalized;
          } else if (firstIssue.loc?.includes?.("name") || firstIssue.loc?.includes?.("key_name")) {
            detail = "Key name must be text or number.";
          }
        }
      } else if (payload.detail && typeof payload.detail === "object") {
        if (typeof payload.detail.message === "string" && payload.detail.message.trim()) {
          detail = payload.detail.message.trim();
        } else {
          detail = JSON.stringify(payload.detail);
        }
      } else if (typeof payload.message === "string" && payload.message.trim()) {
        detail = payload.message.trim();
      }
    }
  } else {
    const text = await response.text().catch(() => "");
    if (text && text.trim()) {
      detail = text.trim();
    }
  }

  if (detail) return detail;
  if (response.status === 401) return "Session expired or invalid. Please sign in again.";
  if (response.status === 403) return "You are not allowed to manage API keys for this account.";
  if (response.status === 404) return "API endpoint not found. Verify backend is running on port 8000.";
  return fallbackMessage;
};

const getNetworkErrorMessage = (err, fallbackMessage) => {
  const msg = String(err?.message || "").toLowerCase();
  if (msg.includes("failed to fetch") || msg.includes("networkerror")) {
    return "Cannot reach backend API. Verify server is running on http://localhost:8000.";
  }
  return err?.message || fallbackMessage;
};

// Countdown hook — returns seconds remaining until ISO expiry string
const useCountdown = (expiresAt) => {
  const [secs, setSecs] = useState(() => {
    if (!expiresAt) return null;
    const diff = Math.floor((new Date(expiresAt) - Date.now()) / 1000);
    return diff > 0 ? diff : 0;
  });
  const timerRef = useRef(null);
  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const diff = Math.floor((new Date(expiresAt) - Date.now()) / 1000);
      setSecs(diff > 0 ? diff : 0);
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => clearInterval(timerRef.current);
  }, [expiresAt]);
  return secs;
};

const CountdownBadge = ({ expiresAt }) => {
  const secs = useCountdown(expiresAt);
  if (secs === null) return null;
  const mins = Math.floor(secs / 60);
  const s = secs % 60;
  const label = secs === 0 ? "Disabled" : `⏱ Disables in ${mins}:${String(s).padStart(2, "0")}`;
  return (
    <span className="text-xs font-semibold text-orange-600 mt-0.5 block">
      {label}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const cfg = {
    active:   { cls: "bg-green-100 text-green-700",  label: "Active" },
    expiring: { cls: "bg-orange-100 text-orange-700", label: "Expiring" },
    disabled: { cls: "bg-gray-100 text-gray-500",     label: "Disabled" },
    expired:  { cls: "bg-red-100 text-red-500",       label: "Expired" },
  }[status] || { cls: "bg-gray-100 text-gray-500", label: status };
  return (
    <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
};

const ApiKeysPage = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const role = localStorage.getItem("role") || "user";

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [rotatingId, setRotatingId] = useState("");
  const [rotateTarget, setRotateTarget] = useState(null); // key object awaiting confirm
  const [error, setError] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null); // { name, api_key }
  const [modalCopied, setModalCopied] = useState(false);
  const [adminRotatedKey, setAdminRotatedKey] = useState(null); // { name, api_key } for admin-rotated reveal
  const [adminRotatedCopied, setAdminRotatedCopied] = useState(false);

  const handleCloseKeyReveal = () => {
    setNewlyCreatedKey(null);
    setModalCopied(false);
  };

  const copyNewKey = async () => {
    if (!newlyCreatedKey?.api_key) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(newlyCreatedKey.api_key);
      } else {
        const el = document.createElement("textarea");
        el.value = newlyCreatedKey.api_key;
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      setModalCopied(true);
      setTimeout(() => setModalCopied(false), 2000);
    } catch {
      setError("Unable to copy. Please select and copy the key manually.");
    }
  };

  const mode = useMemo(() => (role === "agent" || role === "support_agent" ? "agent" : "user"), [role]);

  const fetchKeys = useCallback(async () => {
    if (!getStoredAccessToken()) {
      navigate("/");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await authFetch("/v1/api-keys", {
        method: "GET",
      });
      if (!response.ok) {
        throw new Error(
          await getErrorMessageFromResponse(response, "Failed to load API keys."),
        );
      }
      const payload = await response.json().catch(() => []);
      const STATUS_ORDER = { active: 0, expiring: 1, expired: 2, disabled: 3 };
      const keyList = (Array.isArray(payload) ? payload : []).slice().sort((a, b) => {
        const sa = STATUS_ORDER[a.status] ?? 99;
        const sb = STATUS_ORDER[b.status] ?? 99;
        return sa - sb;
      });
      setKeys(keyList);

      // Auto-reveal any admin-rotated keys (one-time, clears on backend)
      const rotated = keyList.find((k) => k.admin_rotated);
      if (rotated) {
        try {
          const revealRes = await authFetch(`/v1/api-keys/${rotated.key_id}/reveal-rotation`, { method: "POST" });
          if (revealRes.ok) {
            const revealPayload = await revealRes.json().catch(() => ({}));
            if (revealPayload?.api_key) {
              setAdminRotatedKey({ name: rotated.key_name, api_key: String(revealPayload.api_key), key_id: rotated.key_id || "" });
            }
          }
        } catch {
          // Non-fatal — user can still use the key, just can't see the plaintext
        }
        // Refresh to clear admin_rotated flag from UI
        const refreshed = await authFetch("/v1/api-keys").catch(() => null);
        if (refreshed?.ok) {
          const refreshedList = await refreshed.json().catch(() => []);
          const STATUS_ORDER2 = { active: 0, expiring: 1, expired: 2, disabled: 3 };
          const sortedRefreshed = (Array.isArray(refreshedList) ? refreshedList : []).slice().sort((a, b) => (STATUS_ORDER2[a.status] ?? 99) - (STATUS_ORDER2[b.status] ?? 99));
          setKeys(sortedRefreshed);
        }
      }
    } catch (err) {
      setError(getNetworkErrorMessage(err, "Failed to load API keys."));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setNewKeyName("");
  };

  const handleCreateKey = async (event) => {
    event.preventDefault();
    if (!newKeyName.trim()) {
      setError("Please provide a key name.");
      return;
    }
    if (!getStoredAccessToken()) {
      setError("Missing login token. Please sign in again.");
      return;
    }

    setCreating(true);
    setError("");
    try {
      const response = await authFetch("/v1/api-keys", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: newKeyName.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error(
          await getErrorMessageFromResponse(response, "Failed to create API key."),
        );
      }
      const payload = await response.json().catch(() => ({}));
      if (payload?.key_id && payload?.api_key) {
        setNewlyCreatedKey({ name: newKeyName.trim(), api_key: String(payload.api_key), key_id: payload.key_id || "" });
      }

      closeCreateModal();
      await fetchKeys();
    } catch (err) {
      setError(getNetworkErrorMessage(err, "Failed to create API key."));
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!getStoredAccessToken() || !keyId) return;
    setDeletingId(keyId);
    setError("");

    try {
      const response = await authFetch(`/v1/api-keys/${keyId}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error(
          await getErrorMessageFromResponse(response, "Failed to delete API key."),
        );
      }
      await response.json().catch(() => ({}));
      await fetchKeys();
    } catch (err) {
      setError(getNetworkErrorMessage(err, "Failed to delete API key."));
    } finally {
      setDeletingId("");
    }
  };

  const handleRotateKey = async () => {
    const keyId = rotateTarget?.key_id;
    const keyName = rotateTarget?.key_name || "Rotated Key";
    if (!keyId) return;
    setRotateTarget(null);
    setRotatingId(keyId);
    setError("");
    try {
      const response = await authFetch(`/v1/api-keys/${keyId}/rotate`, { method: "POST" });
      if (!response.ok) {
        throw new Error(await getErrorMessageFromResponse(response, "Failed to rotate API key."));
      }
      const payload = await response.json().catch(() => ({}));
      if (payload?.key_id && payload?.api_key) {
        setNewlyCreatedKey({ name: keyName, api_key: String(payload.api_key), key_id: payload.key_id || "" });
      }
      await fetchKeys();
    } catch (err) {
      setError(getNetworkErrorMessage(err, "Failed to rotate API key."));
    } finally {
      setRotatingId("");
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return null;
    return {
      date: date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }),
      time: date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }),
    };
  };

  const DateTimeCell = ({ isoString, label, labelClass = "text-gray-400" }) => {
    const parts = formatDate(isoString);
    if (!parts) return null;
    return (
      <div className="text-xs space-y-0.5">
        {label && <div className={`font-semibold uppercase tracking-wide text-[10px] ${labelClass}`}>{label}</div>}
        <div className="text-gray-700">{parts.date}</div>
        <div className="text-gray-400">{parts.time}</div>
      </div>
    );
  };

  const visibleKeys = keys.filter((item) => {
    const isPastExpiry = item.expires_at ? new Date(item.expires_at) < new Date() : false;
    const st = isPastExpiry && item.status !== "disabled" ? "expired" : item.status;
    return st !== "disabled" && st !== "expired";
  });
  const hasKeys = visibleKeys.length > 0;

  return (
    <div className="flex min-h-screen h-screen w-full bg-[#f5f5f5]">
      <SideBar
        mode={mode}
        onLogout={onLogout}
        isDarkMode={false}
        onLinkClick={(path) => navigate(path)}
        currentPath={location.pathname}
      />

      <div className="flex-1 relative overflow-y-auto">
        <div className="absolute top-4 right-6 z-50">
          <UserProfileMenu onLogout={onLogout} />
        </div>

        <div className="max-w-5xl mx-auto px-6 py-10">
          {!hasKeys && !loading ? (
            <div className="min-h-[60vh] flex flex-col items-center justify-center">
              <div className="w-12 h-12 rounded-xl bg-gray-200 text-gray-700 flex items-center justify-center mb-5">
                <Lock size={22} />
              </div>
              <h1 className="text-2xl font-semibold text-[#1f1f1f] mb-2">API Keys</h1>
              <p className="text-[#3a3a3a] text-lg mb-6">
                Create an API key to access the Arta API
              </p>
              <button
                type="button"
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 rounded-xl bg-[#111111] text-white px-4 py-2 text-base font-medium hover:bg-black"
              >
                <Plus size={14} />
                Create new secret key
              </button>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2 text-[#1f1f1f] font-semibold text-lg">
                  <KeyRound size={18} />
                  API Keys
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={fetchKeys}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <RefreshCw size={14} />
                    Refresh
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(true)}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#111111] text-white text-sm hover:bg-black"
                  >
                    <Plus size={14} />
                    Create new secret key
                  </button>
                </div>
              </div>

              <div className="px-6 py-3 border-b border-gray-100 grid grid-cols-12 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                <div className="col-span-2">Name</div>
                <div className="col-span-2">Secret Key</div>
                <div className="col-span-2">Created</div>
                <div className="col-span-2">Expires</div>
                <div className="col-span-1">Requests</div>
                <div className="col-span-3 text-right">Actions</div>
              </div>

              {loading ? (
                <div className="px-6 py-10 text-sm text-gray-500">Loading keys...</div>
              ) : visibleKeys.length === 0 ? (
                <div className="px-6 py-10 text-sm text-gray-500">No keys available.</div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {visibleKeys.map((item) => {
                    // Client-side expiry check — if expires_at is in the past, treat as expired
                    const isPastExpiry = item.expires_at
                      ? new Date(item.expires_at) < new Date()
                      : false;
                    // If expires_at is in the past and key isn't already disabled, treat as expired
                    // This covers both "active" keys past 30 days AND "expiring" keys past 10-min grace
                    const effectiveStatus =
                      isPastExpiry && item.status !== "disabled"
                        ? "expired"
                        : item.status;
                    const isExpiring = effectiveStatus === "expiring";
                    const isDisabled = effectiveStatus === "disabled" || effectiveStatus === "expired";
                    const canRotate = effectiveStatus === "active";
                    return (
                      <div
                        key={item.key_id}
                        className={`px-6 py-4 grid grid-cols-12 items-center gap-3 ${isDisabled ? "opacity-60" : ""}`}
                      >
                        {/* Name + status badge */}
                        <div className="col-span-2 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-[#1f1f1f] truncate">{item.key_name}</span>
                            <StatusBadge status={effectiveStatus} />
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5 truncate">{item.key_id}</div>
                          {isExpiring && <CountdownBadge expiresAt={item.expires_at} />}
                        </div>

                        {/* Secret — masked hint using last 4 chars of key_id */}
                        <div className="col-span-2 min-w-0 overflow-hidden">
                          <code className="text-sm text-gray-500 bg-gray-50 border border-gray-200 px-2.5 py-1.5 rounded-md block truncate max-w-full">
                            ••••••••••...{item.key_id?.slice(-4) || "????"}
                          </code>
                        </div>

                        {/* Created */}
                        <div className="col-span-2">
                          <DateTimeCell isoString={item.created_at} />
                        </div>

                        {/* Expires */}
                        <div className="col-span-2">
                          {item.expires_at
                            ? <DateTimeCell isoString={item.expires_at} />
                            : <span className="text-xs text-gray-400">—</span>}
                        </div>

                        {/* Requests */}
                        <div className="col-span-1 text-sm text-gray-600">
                          {Number(item.total_requests || 0)}
                        </div>

                        {/* Actions */}
                        <div className="col-span-3 flex justify-end items-center gap-1.5 flex-wrap">
                            {/* Rotate */}
                          {canRotate && (
                            <button
                              type="button"
                              disabled={rotatingId === item.key_id}
                              onClick={() => setRotateTarget(item)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border border-cyan-200 text-cyan-700 hover:bg-cyan-50 disabled:opacity-50"
                              title="Rotate key — generates a new key, old key lives 10 min"
                            >
                              {rotatingId === item.key_id
                                ? <RefreshCw size={13} className="animate-spin" />
                                : <RotateCcw size={13} />}
                              Rotate
                            </button>
                          )}

                          {/* Delete */}
                          {!isDisabled && (
                            <button
                              type="button"
                              disabled={deletingId === item.key_id}
                              onClick={() => handleDeleteKey(item.key_id)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                            >
                              {deletingId === item.key_id
                                ? <RefreshCw size={13} className="animate-spin" />
                                : <Trash2 size={13} />}
                              {deletingId === item.key_id ? "Disabling" : "Delete"}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="mt-5 p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700 whitespace-pre-wrap break-words">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Rotate confirmation modal */}
      {rotateTarget && (
        <div className="fixed inset-0 z-[220] bg-black/40 flex items-center justify-center px-4">
          <div className="w-full max-w-md bg-white border border-gray-200 rounded-2xl shadow-xl">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#1f1f1f]">Rotate API Key?</h2>
              <button type="button" onClick={() => setRotateTarget(null)} className="text-gray-500 hover:text-gray-700">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-700">
                This will generate a <strong>brand new secret key</strong>. To prevent downtime, your current key{" "}
                <span className="font-mono text-xs bg-gray-100 px-1 rounded">{rotateTarget.key_name}</span>{" "}
                will remain active for <strong>10 minutes</strong> before it is permanently disabled.
              </p>
              <p className="text-sm text-orange-600 font-medium">
                ⚠ Please update your backend with the new key immediately after rotating.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setRotateTarget(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleRotateKey}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-600 text-white text-sm hover:bg-cyan-700"
                >
                  <RotateCcw size={14} />
                  Rotate Key
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 z-[220] bg-black/40 flex items-center justify-center px-4">
          <div className="w-full max-w-md bg-white border border-gray-200 rounded-2xl shadow-xl">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#1f1f1f]">Create new secret key</h2>
              <button
                type="button"
                onClick={closeCreateModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateKey} className="p-5 space-y-4">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Key name</label>
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(event) => setNewKeyName(event.target.value)}
                  placeholder="Example: production-app"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  required
                />
              </div>

              <div className="text-xs text-gray-500">
                Keep this key secure. You can delete it anytime from the keys table.
              </div>

              <div className="pt-2 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#111111] text-white text-sm hover:bg-black disabled:opacity-60"
                >
                  {creating ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={14} />}
                  {creating ? "Creating..." : "Create key"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Key reveal modal — shown once after create or rotate */}
      {newlyCreatedKey && (
        <div className="fixed inset-0 z-[230] bg-black/50 flex items-center justify-center px-4">
          <div className="w-full max-w-lg bg-white border border-gray-200 rounded-2xl shadow-xl">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#1f1f1f]">Save your secret key</h2>
              <button type="button" onClick={handleCloseKeyReveal} className="text-gray-400 hover:text-gray-700">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                <span className="text-amber-500 text-lg leading-none mt-0.5">⚠</span>
                <p className="text-sm text-amber-800 font-medium">
                  Please copy this key and store it somewhere safe. <strong>For security reasons, you will not be able to see it again</strong> after you close this window.
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Secret key — {newlyCreatedKey.name}
                </label>
                {newlyCreatedKey.key_id && (
                  <p className="text-xs text-gray-400 mb-2 font-mono">Key ID: {newlyCreatedKey.key_id}</p>
                )}
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm text-gray-600 bg-gray-50 border border-gray-200 px-3 py-2.5 rounded-lg font-mono">
                    {"•".repeat(24)}...{newlyCreatedKey.api_key.slice(-4)}
                  </code>
                  <button
                    type="button"
                    onClick={copyNewKey}
                    className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium border transition ${
                      modalCopied
                        ? "bg-green-50 border-green-300 text-green-700"
                        : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {modalCopied ? <CheckCircle2 size={15} /> : <Copy size={15} />}
                    {modalCopied ? "Copied!" : "Copy"}
                  </button>
                </div>
              </div>

              <div className="pt-2 flex justify-end">
                <button
                  type="button"
                  onClick={handleCloseKeyReveal}
                  className="px-5 py-2 rounded-lg bg-[#111111] text-white text-sm font-medium hover:bg-black"
                >
                  I've saved my key, close this window
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Admin-rotated key reveal modal */}
      {adminRotatedKey && (
        <div className="fixed inset-0 z-[240] bg-black/50 flex items-center justify-center px-4">
          <div className="w-full max-w-lg bg-white border border-gray-200 rounded-2xl shadow-xl">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#1f1f1f]">Your API key was rotated by an admin</h2>
              <button type="button" onClick={() => { setAdminRotatedKey(null); setAdminRotatedCopied(false); }} className="text-gray-400 hover:text-gray-700">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                <span className="text-amber-500 text-lg leading-none mt-0.5">⚠</span>
                <p className="text-sm text-amber-800 font-medium">
                  An administrator rotated your key <strong>{adminRotatedKey.name}</strong>. Your old key will stop working soon. Copy this new key now — <strong>you will not be able to see it again</strong> after closing this window.
                </p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  New key — {adminRotatedKey.name}
                </label>
                {adminRotatedKey.key_id && (
                  <p className="text-xs text-gray-400 mb-2 font-mono">Key ID: {adminRotatedKey.key_id}</p>
                )}
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm text-gray-600 bg-gray-50 border border-gray-200 px-3 py-2.5 rounded-lg font-mono">
                    {"•".repeat(24)}...{adminRotatedKey.api_key.slice(-4)}
                  </code>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        if (navigator.clipboard && window.isSecureContext) {
                          await navigator.clipboard.writeText(adminRotatedKey.api_key);
                        } else {
                          const el = document.createElement("textarea");
                          el.value = adminRotatedKey.api_key;
                          el.style.position = "fixed";
                          el.style.opacity = "0";
                          document.body.appendChild(el);
                          el.select();
                          document.execCommand("copy");
                          document.body.removeChild(el);
                        }
                        setAdminRotatedCopied(true);
                        setTimeout(() => setAdminRotatedCopied(false), 2000);
                      } catch {
                        setError("Unable to copy. Please copy manually.");
                      }
                    }}
                    className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium border transition ${
                      adminRotatedCopied
                        ? "bg-green-50 border-green-300 text-green-700"
                        : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {adminRotatedCopied ? <CheckCircle2 size={15} /> : <Copy size={15} />}
                    {adminRotatedCopied ? "Copied!" : "Copy"}
                  </button>
                </div>
              </div>
              <div className="pt-2 flex justify-end">
                <button
                  type="button"
                  onClick={() => { setAdminRotatedKey(null); setAdminRotatedCopied(false); }}
                  className="px-5 py-2 rounded-lg bg-[#111111] text-white text-sm font-medium hover:bg-black"
                >
                  I've saved my new key, close this
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeysPage;
